import React from 'react';
import { Tabs, Tab, Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import api from 'api';
import useRequest from 'hooks/useRequest';
import useUser from 'hooks/useUser';
import { BurnCenterType } from 'types/burnCenter';

import { FlexCenter } from 'resources/styles/display';
import Loader from 'components/elements/Loader';
import AboutTab from 'pages/burn-centers/admin/details/components/AboutTab';
import BenchmarkTab from 'pages/burn-centers/admin/details/components/BenchmarkTab';

export const BurnCenterTabLabels = {
  About: 'About',
  BenchmarkCriteria: 'Bed Capacity',
} as const;

type TabKey = keyof typeof BurnCenterTabLabels;

const ProfilePage: React.FC = () => {
  const {
    isLoading,
    error,
    doRequest: fetchCurrent,
    data: burnCenter,
  } = useRequest<BurnCenterType>(api.getCurrentBurnCenter);

  React.useEffect(() => {
    fetchCurrent(null);
  }, [fetchCurrent]);

  const {
    isAdmin, isBdataAmin, isAbaAdmin, isBcAdmin,
  } = useUser();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = (searchParams.get('tab') || 'About') as TabKey;
  const [activeTab, setActiveTab] = React.useState<TabKey>(urlTab);

  React.useEffect(() => {
    const t = (searchParams.get('tab') || 'About') as TabKey;
    if (t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onTabChange = (key: string | null) => {
    if (!key) return;
    setActiveTab(key as TabKey);
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container className="my-5 site-content">
      {isLoading && (
        <FlexCenter
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
          }}
        >
          <Loader />
        </FlexCenter>
      )}

      {error && <div className="text-danger">Failed to load Burn Center.</div>}

      {!isLoading && burnCenter && (
        <>
          <h2 className="mb-2">
            {burnCenter.name}
            {' '}
            (
            {burnCenter.hospitalName}
            )
          </h2>

          <Tabs
            id="burncenter-profile-tabs"
            activeKey={activeTab}
            onSelect={onTabChange}
            mountOnEnter
          >
            <Tab eventKey="About" title={BurnCenterTabLabels.About}>
              <div className="border border-top-0 p-3">
                <AboutTab
                  id={String(burnCenter.id)}
                  canEdit={isBcAdmin}
                  canEditCenterIdentity={isBdataAmin}
                  benchmark={burnCenter.benchmark ?? null}
                  onSaved={() => fetchCurrent(null)}
                  values={{
                    name: burnCenter.name,
                    hospitalName: burnCenter.hospitalName,
                    addressLine1: burnCenter.addressLine1,
                    addressLine2: burnCenter.addressLine2,
                    addressLine3: burnCenter.addressLine3,
                    city: burnCenter.city,
                    region: burnCenter.region,
                    zipPostalCode: burnCenter.zipPostalCode,
                    location: burnCenter.location ?? null,
                    country: burnCenter.country ?? null,
                    phoneNumber: burnCenter.phoneNumber,
                    tollFreeNumber: burnCenter.tollFreeNumber,
                    faxNumber: burnCenter.faxNumber,
                    email: burnCenter.email,
                    website: burnCenter.website,
                  }}
                />
              </div>
            </Tab>

            <Tab
              eventKey="BenchmarkCriteria"
              title={BurnCenterTabLabels.BenchmarkCriteria}
            >
              <div className="border border-top-0 p-3">
                <BenchmarkTab
                  id={String(burnCenter.id)}
                  canEdit={isBdataAmin || isAbaAdmin || isAdmin || isBcAdmin}
                  values={burnCenter.benchmark ?? {}}
                  onSaved={() => fetchCurrent(null)}
                />
              </div>
            </Tab>
          </Tabs>
        </>
      )}
    </Container>
  );
};

export default ProfilePage;
